import { useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { useGlobalEffectActions, usePageContext } from "@ryde/redux";
import {
  useIsMobile,
  UserEditDisplayModeEnum,
  userEditOnlyDisplayModeAtomFamily,
} from "@ryde/shared";

import { useUserUpdaterRideToggle } from "./use-user-updater-ride-toggle";

export const useUserUpdaterRideToggleInternal = () => {
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const { resetPaginationAppEvent } = useGlobalEffectActions();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = useRecoilState(
    userEditOnlyDisplayModeAtomFamily(entity),
  );

  const { ridesCountByViewMode, optionTextByValue } =
    useUserUpdaterRideToggle();

  const setSelectedValueHandler = useEvent((value: UserEditDisplayModeEnum) => {
    setSelectedValue(value);
    resetPaginationAppEvent();
  });

  const handleClick = (): void => {
    const enumValues = Object.values(UserEditDisplayModeEnum);
    const currentIndex = enumValues.indexOf(selectedValue);
    const nextIndex = (currentIndex + 1) % enumValues.length;
    setSelectedValueHandler(enumValues[nextIndex]);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    value: UserEditDisplayModeEnum,
  ): void => {
    event.preventDefault();
    setSelectedValue(value);
    setOpen(false);
  };

  const handleToggle = (): void => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const ridesCountBySelection = useMemo(() => {
    return ridesCountByViewMode(selectedValue);
  }, [ridesCountByViewMode, selectedValue]);

  return {
    anchorRef,
    open,
    isMobile,
    selectedValue,
    optionTextByValue,
    ridesCountBySelection,
    handleClick,
    handleToggle,
    handleClose,
    handleMenuItemClick,
    ridesCountByViewMode,
  };
};
